import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Filter, MapPin, DollarSign, Clock, Star, ArrowLeft, Package, ShoppingBag, FileText, Briefcase, Truck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Card } from '../../../components/ui/card';
import { AppLayout } from '../../../components/layout/AppLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SERVICE_CATEGORIES = [
  { value: 'delivery', label: 'Delivery', icon: Package, color: 'text-blue-600' },
  { value: 'shopping_assistance', label: 'Shopping', icon: ShoppingBag, color: 'text-green-600' },
  { value: 'document_collection', label: 'Documents', icon: FileText, color: 'text-purple-600' },
  { value: 'errands', label: 'Errands', icon: Briefcase, color: 'text-orange-600' },
  { value: 'small_transport', label: 'Small Transport', icon: Truck, color: 'text-red-600' },
];

const BrowseJobsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    service_category: searchParams.get('category') || '',
    area: '',
    min_price: '',
    max_price: '',
    country: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/pickup/available`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setJobs(response.data.deliveries || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Filter jobs based on search and filters
  const filteredJobs = jobs.filter(job => {
    // Search filter
    if (searchTerm && !job.task_description?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !job.pickup_area?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !job.destination_area?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Service category filter
    if (filters.service_category && job.service_category !== filters.service_category) {
      return false;
    }

    // Area filter
    if (filters.area && !job.pickup_area?.toLowerCase().includes(filters.area.toLowerCase()) &&
        !job.destination_area?.toLowerCase().includes(filters.area.toLowerCase())) {
      return false;
    }

    // Price filters
    if (filters.min_price && job.delivery_fee < parseFloat(filters.min_price)) {
      return false;
    }
    if (filters.max_price && job.delivery_fee > parseFloat(filters.max_price)) {
      return false;
    }

    // Country filter
    if (filters.country && !job.pickup_country?.toLowerCase().includes(filters.country.toLowerCase())) {
      return false;
    }

    return true;
  });

  const JobCard = ({ job }) => {
    const categoryInfo = SERVICE_CATEGORIES.find(c => c.value === job.service_category) || SERVICE_CATEGORIES[0];
    const CategoryIcon = categoryInfo.icon;

    return (
      <Card 
        className="p-4 hover:shadow-lg transition-all cursor-pointer border-2 hover:border-blue-500"
        onClick={() => navigate(`/pickup/delivery/${job.id}`)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 ${categoryInfo.color}`}>
              <CategoryIcon className="h-5 w-5" />
            </div>
            <div>
              <Badge variant="secondary" className="text-xs">
                {categoryInfo.label}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600">{job.currency} {job.delivery_fee}</p>
            <p className="text-xs text-muted-foreground">Fixed Price</p>
          </div>
        </div>

        {/* Task Description */}
        <h3 className="font-semibold text-lg mb-2 line-clamp-2">
          {job.task_description || 'Job request'}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <MapPin className="h-4 w-4" />
          <span className="line-clamp-1">
            {job.pickup_area}, {job.pickup_city} → {job.destination_area}, {job.destination_city}
          </span>
        </div>

        {/* Customer Info */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
              {job.customer_name?.[0] || 'C'}
            </div>
            <div>
              <p className="text-sm font-medium">{job.customer_name || 'Customer'}</p>
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <span className="text-xs text-muted-foreground">
                  {job.customer_rating ? job.customer_rating.toFixed(1) : 'New'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{job.pickup_date}</span>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 border-b sticky top-0 z-10 px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pickup')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Browse Jobs
              </h1>
              <p className="text-sm text-muted-foreground">
                {filteredJobs.length} available {filteredJobs.length === 1 ? 'job' : 'jobs'}
              </p>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search jobs by description or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Service Category */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Service Type</label>
                  <select
                    className="w-full p-2 border rounded-lg bg-white dark:bg-gray-900"
                    value={filters.service_category}
                    onChange={(e) => setFilters({ ...filters, service_category: e.target.value })}
                  >
                    <option value="">All Services</option>
                    {SERVICE_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Area */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Area</label>
                  <Input
                    placeholder="e.g., Sandton"
                    value={filters.area}
                    onChange={(e) => setFilters({ ...filters, area: e.target.value })}
                  />
                </div>

                {/* Price Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Min Price</label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.min_price}
                      onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Max Price</label>
                    <Input
                      type="number"
                      placeholder="1000"
                      value={filters.max_price}
                      onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFilters({ service_category: '', area: '', min_price: '', max_price: '', country: '' });
                    setSearchTerm('');
                  }}
                >
                  Clear All
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Jobs Grid */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
              <p className="mt-4 text-muted-foreground">Loading jobs...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No jobs found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || Object.values(filters).some(v => v) 
                  ? 'Try adjusting your filters or search term'
                  : 'Be the first to post a job!'}
              </p>
              <Button onClick={() => navigate('/pickup/post')}>
                Post a Job
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredJobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default BrowseJobsPage;
